import React from 'react';

import { ArrowUpward, Code, Delete, Info, Refresh } from '@mui/icons-material';
import { Alert, Box, Button, Chip, IconButton, Stack, Typography, useTheme } from '@mui/material';

import { EnvironmentModel } from '../../../api/api';
import { getNodePalette } from './EnvironmentNodePalette';
import { useEmbedDefinitions, useEmbedDeployments } from './useEmbeds';

const actionStyle = { padding: '1px', marginLeft: '2px', color: 'inherit', '& .MuiSvgIcon-root': { fontSize: '10px' } };
const nameStyle = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 };

export default function EnvironmentEmbedsPanel({
	environment,
	onSelect,
}: {
	environment: EnvironmentModel;
	onSelect?: (environment: EnvironmentModel, definitionId?: string, deploymentId?: string, action?: 'remove') => void;
}) {
	const palette = getNodePalette(useTheme());
	const definitions = useEmbedDefinitions();
	const deployments = useEmbedDeployments(environment.id);
	const available = definitions.data?.filter((definition) => !deployments.data?.some((deployment) => deployment.definitionId === definition.id));
	const rowStyle = { fontSize: '10px', padding: '2px 6px', borderRadius: '4px', textAlign: 'left', minWidth: 0 };
	return (
		<Box sx={{ mt: '10px' }} className="nodrag nopan" onClick={(event) => event.stopPropagation()}>
			<Box sx={{ mb: '8px' }}>
				<Typography component="div" sx={{ fontSize: '12px', fontWeight: 'bold', mb: '4px', color: palette.accent }}>
					Deployed App Embeds
				</Typography>
				{(definitions.isError || deployments.isError) && (
					<Alert
						severity="error"
						sx={{ fontSize: '10px', py: '4px', px: '6px', '& .MuiAlert-icon': { fontSize: '14px', mr: '6px' }, '& .MuiAlert-message': { py: 0 } }}
					>
						App Embeds could not be loaded.{' '}
						<Button
							onClick={() => {
								void definitions.refetch();
								void deployments.refetch();
							}}
						>
							Retry
						</Button>
					</Alert>
				)}
				<Stack spacing="2px">
					{deployments.data?.map((deployment) => (
						<Stack
							key={deployment.id}
							direction="row"
							alignItems="center"
							justifyContent="space-between"
							title={`${deployment.name} - ${deployment.publishableKey ? 'Deployed' : 'Pending'}`}
							sx={{
								...rowStyle,
								bgcolor: palette.itemSurface,
								color: palette.text,
								border: deployment.publishableKey ? `1px solid ${palette.accentStrong}` : `1px dashed ${palette.accent}`,
							}}
						>
							<Stack direction="row" alignItems="center" spacing="4px" sx={{ minWidth: 0, flex: 1 }}>
								<Code sx={{ fontSize: '10px', flexShrink: 0 }} />
								<Typography component="span" sx={{ ...nameStyle, fontSize: 'inherit' }}>
									{deployment.name}
								</Typography>
							</Stack>
							<Stack direction="row" alignItems="center" sx={{ flexShrink: 0 }}>
								{deployment.updateAvailable && <Chip label="Update available" color="warning" sx={{ fontSize: '8px', height: 14 }} />}
								{!deployment.isEnabled && <Chip label="Disabled" sx={{ fontSize: '8px', height: 14 }} />}
								<IconButton
									size="small"
									title="Redeploy App Embed"
									sx={actionStyle}
									disabled={!environment.changeSetId || !deployment.definitionId}
									onClick={() => onSelect?.(environment, deployment.definitionId)}
								>
									<Refresh />
								</IconButton>
								<IconButton
									size="small"
									title="App Embed details"
									sx={actionStyle}
									onClick={() => onSelect?.(environment, deployment.definitionId, deployment.id)}
								>
									<Info />
								</IconButton>
								<IconButton
									size="small"
									title="Remove App Embed"
									sx={actionStyle}
									onClick={() => onSelect?.(environment, deployment.definitionId, deployment.id, 'remove')}
								>
									<Delete />
								</IconButton>
							</Stack>
						</Stack>
					))}
					{deployments.data?.length === 0 && (
						<Box
							sx={{
								fontSize: '10px',
								padding: '4px 6px',
								borderRadius: '4px',
								bgcolor: palette.emptySurface,
								color: palette.textMuted,
								border: `1px dashed ${palette.emptyBorder}`,
								textAlign: 'center',
								fontStyle: 'italic',
							}}
						>
							No deployed App Embeds
						</Box>
					)}
				</Stack>
			</Box>
			{!!available?.length && (
				<Typography component="div" sx={{ mb: '4px', fontSize: '12px', fontWeight: 'bold', color: palette.textMuted }}>
					Available App Embeds
				</Typography>
			)}
			<Stack spacing="2px">
				{available?.map((definition) => (
					<Stack
						key={definition.id}
						direction="row"
						alignItems="center"
						justifyContent="space-between"
						title={`${definition.name} - Click arrow to deploy to environment`}
						sx={{ ...rowStyle, bgcolor: palette.itemSurfaceMuted, color: palette.textMuted, border: `1px solid ${palette.borderMuted}` }}
					>
						<Typography component="span" sx={{ ...nameStyle, fontSize: 'inherit' }}>
							{definition.name}
						</Typography>
						<IconButton
							title="Deploy App Embed"
							disabled={!environment.changeSetId}
							size="small"
							sx={{ width: '16px', height: '16px', fontSize: '8px', color: 'inherit', marginLeft: '4px' }}
							onClick={() => onSelect?.(environment, definition.id)}
						>
							<ArrowUpward sx={{ fontSize: '10px' }} />
						</IconButton>
					</Stack>
				))}
			</Stack>
		</Box>
	);
}
